import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { Drawer, Button, Box, IconButton, Grid, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InputField from "../../components/InputField";
import { t } from "i18next";
import styles from "./styles";
import CustomSelect from "../../components/SelectField";
import { Status } from "./types";
import { useEffect, useState } from "react";
import { IRole } from "../AddUser/types";
import { getApiErrorMessage } from "../../utils/commonHelpers";
import getAPIUrl from "../../config";
import { getApi } from "../../utils/apis";
import { useTranslation } from "react-i18next";

type FilterInputs = {
    name: string;
    email: string;
    phone: string;
    roleId: string;
    status: string;
};

type FilterDrawerProps = {
    drawerOpen: boolean;
    toggleDrawer: (open: boolean) => () => void;
    handleApplyFilters: (filters: FilterInputs) => void;
};

const defaultValues = {
    name: "",
    roleId: "",
    email: "",
    phone: "",
    status: ""
};


const statusList = Object.entries(Status).map((rec) => ({
    name: rec[0],
    value: rec[1],
}));



const FilterDrawer = ({ drawerOpen, toggleDrawer, handleApplyFilters }: FilterDrawerProps) => {
    const [roles, setRoles] = useState<IRole[]>([]);
    const [errorToast, setErrorToast] = useState<boolean>(false);
    const [toastErrorMsg, setErrorToastMsg] = useState<string>("");

    const { formState: { errors }, control, handleSubmit, reset } = useForm<FilterInputs>({ defaultValues: defaultValues });
    
       const { t } = useTranslation();


    useEffect(() => {
        const getRoles = async () => {
            try {
                const rolesData = await getApi(`${getAPIUrl()}/api/v1/roles/name`);
                setRoles(rolesData.data);
            } catch (error) {
                if (error instanceof Error) {
                    const errorMsg = getApiErrorMessage(error);
                    setErrorToast(true);
                    setErrorToastMsg(errorMsg);
                }
            }
        }

        getRoles();
    }, [])
    const onSubmit: SubmitHandler<FilterInputs> = (data) => {
        handleApplyFilters(data);
        toggleDrawer(false)();
    };

    const handleClear = () => {
        reset(); // Clear the form fields
        handleApplyFilters({ name: "", email: "", phone: "", roleId: "", status: "" }); // Clear filters
        // toggleDrawer(false)();
    };
    const rolesList =
        roles && roles.map((role) => ({ name: role.name, value: role.id }));




    return (
        <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)} sx={styles.filterDrawer}  >

            <Box component="form" sx={{ ...styles.formContainer, paddingLeft: "1rem", paddingRight: "1rem" }} onSubmit={handleSubmit(onSubmit)}>
                <Typography sx={styles.subHeading}>{t("users")}</Typography>
                <IconButton
                    onClick={toggleDrawer(false)}
                    sx={{ position: "absolute", top: 8, right: 8 }}
                >
                    <CloseIcon />
                </IconButton>
                <Grid container spacing={2} xs={14} >
                    <Grid item xs={6}>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    id="name"
                                    label={t("fullName")}
                                    placeholder={t("fullName")}
                                    type="text"
                                    showCross
                                    fullWidth
                                    size="small"
                                    handleCrossClick={() => field.onChange("")}
                                    error={!!errors.name?.message}
                                    errorMessage={errors.name?.message}
                                />
                            )}
                        />

                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    id="email"
                                    label={t("email")}
                                    placeholder={t("email")}
                                    type="text"
                                    showCross
                                    fullWidth
                                    size="small"
                                    handleCrossClick={() => field.onChange("")}
                                    error={!!errors.email?.message}
                                    errorMessage={errors.email?.message}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="phone"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    id="phone"
                                    label={t("phone")}
                                    placeholder={t("phone")}
                                    type="text"
                                    showCross
                                    fullWidth
                                    size="small"
                                    handleCrossClick={() => field.onChange("")}
                                    error={!!errors.phone?.message}
                                    errorMessage={errors.phone?.message}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="roleId"
                            control={control}
                            render={({ field }) => (
                                <CustomSelect
                                    {...field}
                                    id="roleId"
                                    onChange={(e) => field.onChange(e.target.value)}
                                    size="small"
                                    fullWidth
                                    placeholder={t("userType")}
                                    items={rolesList}
                                    error={!!errors.roleId?.message}
                                    errorMessage={errors.roleId?.message}
                                    label={t("userType")}
                                />
                            )}
                        />

                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <CustomSelect
                                    {...field}
                                    id="status"
                                    onChange={(e) => field.onChange(e.target.value)}
                                    size="small"
                                    fullWidth
                                    placeholder="Status"
                                    items={statusList}
                                    error={!!errors.status?.message}
                                    errorMessage={errors.status?.message}
                                    label="Status"
                                />

                            )}
                        />

                    </Grid>
                </Grid>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "8px",
                        marginTop: "16px",
                        paddingBottom: "3rem",
                    }}
                >
                    <Button
                        variant="outlined"
                        color="secondary"
                        fullWidth
                        onClick={handleClear}
                    >
                           {t("clear")}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                    >
                          {t("filter")}
                    </Button>
                </Box>

            </Box>

        </Drawer>
    );
};

export default FilterDrawer;
